import { Link } from 'react-router-dom';
import { categories } from '@/data/categories';

const hubSlugs: Record<string, string> = {
  'mobile-living': 'van-life-basics-complete-guide',
  'off-grid-homes': 'off-grid-living-basics-complete-guide',
  'boat-living': 'boat-living-basics-complete-guide',
  'camping': 'camping-basics-complete-guide'
};

const hubTitles: Record<string, string> = {
  'mobile-living': 'Van Life Basics Guide',
  'off-grid-homes': 'Off-Grid Living Guide',
  'boat-living': 'Boat Living Guide',
  'camping': 'Camping Basics Guide'
};

const pathDescriptions: Record<string, { headline: string; description: string }> = {
  'mobile-living': {
    headline: 'The Road is Your Home',
    description: "Imagine waking up to a different view every morning. Van life, bus conversions, and mobile tiny homes offer the ultimate freedom — work from anywhere, explore everywhere, and carry your entire life with you. From weekend warriors to full-time nomads, the mobile living community has mastered the art of home on wheels. Start with choosing your vehicle, learn the conversion basics, and discover what makes this lifestyle so addictive."
  },
  'off-grid-homes': {
    headline: 'Self-Sufficient Sanctuary',
    description: "Break free from utility bills and build a life powered by nature. Off-grid homes — from rustic cabins to modern tiny houses and innovative dome homes — represent the pinnacle of self-reliance. Learn to harness solar power, collect rainwater, manage waste sustainably, and heat your space without the grid. This path combines traditional homesteading wisdom with cutting-edge sustainable technology."
  },
  'boat-living': {
    headline: 'Life on the Water',
    description: "Trade your landlocked life for the gentle rhythm of waves. Living aboard a sailboat, houseboat, or yacht isn't just a housing choice — it's a complete lifestyle transformation. From marina communities to blue-water sailing, boat living offers adventure, simplicity, and a perspective that only life on the water can provide. Discover the realities of liveaboard life, marine systems, and what it takes to make the water your permanent address."
  },
  'camping': {
    headline: 'Back to the Wild',
    description: "Strip away the unnecessary and rediscover what matters. Camping — from weekend tent trips to extended overlanding expeditions — reconnects you with nature and yourself. Master outdoor cooking, learn wilderness skills, find the gear that actually works, and discover the profound peace of sleeping under the stars. This is where many nomads begin their journey toward a freer life."
  }
};

export const FourPathsSection = () => {
  return (
    <section id="categories" className="py-16 md:py-24 bg-secondary/30" aria-label="Four paths to freedom">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="font-serif text-3xl md:text-4xl font-semibold text-primary mb-4">
            Four Paths to Freedom
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Every journey begins with a choice. Which path calls to you?
          </p>
        </div>

        <div className="space-y-0 max-w-5xl mx-auto">
          {categories.map((category, index) => {
            const pathContent = pathDescriptions[category.id];
            const hubSlug = hubSlugs[category.id];
            const hubTitle = hubTitles[category.id];

            return (
              <article 
                key={category.id}
                className="relative bg-card p-8 md:p-10 border border-border/40 hover:border-primary/30 transition-all duration-700 animate-fade-in -mt-px first:mt-0"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Top decorative line */}
                {index === 0 && (
                  <div className="absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
                )}

                <div className="flex items-start gap-6 md:gap-10">
                  {/* Number indicator */}
                  <div className="flex-shrink-0 pt-1">
                    <span className="text-xs font-light tracking-[0.3em] text-muted-foreground/50 uppercase">
                      0{index + 1}
                    </span>
                  </div>

                  <div className="flex-1">
                    <Link 
                      to={`/category/${category.id}`}
                      className="group"
                    >
                      <h3 className="font-serif text-2xl md:text-3xl font-medium text-primary group-hover:text-accent transition-colors duration-500 mb-2">
                        {pathContent?.headline || category.name}
                      </h3>
                    </Link>
                    <p className="text-[10px] tracking-luxury uppercase text-accent/80 font-normal mb-4">
                      {category.name}
                    </p>
                    <p className="text-foreground/80 leading-relaxed text-lg mb-6 font-light">
                      {pathContent?.description || category.description}
                    </p>
                    
                    <div className="flex flex-wrap items-center gap-4">
                      {hubSlug && (
                        <Link
                          to={`/article/${hubSlug}`}
                          className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 text-[10px] tracking-luxury uppercase font-normal hover:bg-primary/90 transition-colors duration-500"
                        >
                          {hubTitle}
                          <span>→</span>
                        </Link>
                      )}
                      <Link
                        to={`/category/${category.id}`}
                        className="inline-flex items-center gap-2 text-accent/80 hover:text-accent text-[10px] tracking-luxury uppercase font-normal transition-colors duration-500"
                      >
                        Browse all {category.name} articles →
                      </Link>
                    </div>
                  </div>
                </div>

                {/* Bottom decorative line */}
                <div className="absolute bottom-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
